/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'underscore'
], function(ClassFactory, _) {

	/**
	 *	Helper class used to create the required dashboard extensions.
	 * This includes the board model, layout and canvas extensions as well as the dashboard service extensions.
	 */
	class Extensions {

		constructor(dashboardApi) {
			this.dashboardApi = dashboardApi;
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');

			this.consumeViews = {
				absolute: 'dashboard-core/js/dashboard/layout/views/LayoutBaseView',
				genericPage: 'dashboard-core/js/dashboard/layout/views/GenericPage',
				tab: 'dashboard-core/js/dashboard/layout/views/Tab',
				hub: 'dashboard-core/js/dashboard/layout/views/Tab',
				longscroll: 'dashboard-core/js/dashboard/layout/views/Tab',
				group: 'dashboard-core/js/dashboard/layout/views/LayoutBaseView',
				singlePage: 'dashboard-core/js/dashboard/layout/views/SinglePage'
			};

			this.authoringViews = {
				absolute: 'dashboard-core/js/dashboard/layout/authoring/views/Absolute',
				tab: 'dashboard-core/js/dashboard/layout/authoring/views/Tab',
				hub: 'dashboard-core/js/dashboard/layout/authoring/views/Tab',
				longscroll: 'dashboard-core/js/dashboard/layout/authoring/views/Tab',
				group: 'dashboard-core/js/dashboard/layout/authoring/views/Group',
				templateDropZone: 'dashboard-core/js/dashboard/layout/authoring/views/TemplateDropZone',
				templateIndicator: 'dashboard-core/js/dashboard/layout/authoring/views/TemplateIndicator',
				scalingAbsolute: 'dashboard-core/js/dashboard/layout/authoring/views/ScalingAbsolute',
				genericPage: 'dashboard-core/js/dashboard/layout/authoring/views/GenericPage',
				container: 'dashboard-core/js/dashboard/layout/authoring/views/TemplateContainer',
				singlePage: 'dashboard-core/js/dashboard/layout/authoring/views/SinglePage'
			};
		}

		getBoardModelExtensions() {
			return this.boardModelExtensions;
		}

		getLayoutModelExtensions() {
			return this.layoutExtensions && this.layoutExtensions.modelExtensions;
		}

		getLayoutViewExtensions() {
			if (!this.layoutViewExtensions){
				const consumeViews = this.layoutExtensions && this.layoutExtensions.consumeViews;
				if (consumeViews){
					Object.keys(consumeViews).forEach(function (key) {
						this.consumeViews[key] = consumeViews[key];
					}.bind(this));
				}

				const authoringViews = this.layoutExtensions && this.layoutExtensions.authoringViews;
				if (authoringViews){
					Object.keys(authoringViews).forEach(function (key) {
						this.authoringViews[key] = authoringViews[key];
					}.bind(this));
				}

				this.layoutViewExtensions =  {
					authoringViews: this.authoringViews,
					consumeViews: this.consumeViews,
					modelExtensions: this.layoutExtensions && this.layoutExtensions.modelExtensions
				};
			}

			return this.layoutViewExtensions;
		}



		getCanvasExtensions(){
			return this.canvasExtensions;
		}

		load() {
			return this.loadLayoutAndCanvasExtensions().then((extensions) => {
				this.layoutExtensions = extensions[0];
				this.canvasExtensions = extensions[1];
				return this.loadBoardModelExtensions().then((boardModelExtensions) => {
					this.boardModelExtensions = boardModelExtensions;
				});
			});
		}

		loadLayoutAndCanvasExtensions() {
			return Promise.all([this._getLayoutExtensions(), this._getCanvasExtensions()]).then(function(result) {
				return result;

			}.bind(this));
		}

		_getLayoutExtensions() {
			var promise = Promise.resolve();
			var layoutExtensions = this.dashboardApi.getCollectionConfig('layoutExtension');
			if (layoutExtensions) {
				var collectionId = layoutExtensions.id;
				promise = Promise.all([this.dashboardApi.findGlassCollection(collectionId)]).then(collections => {
					const collection = collections[0] || [];
					return this.loadLayoutModelExtensions(collection);
				});
			}
			return promise;
		}
		loadLayoutModelExtensions(collections){
			const combinedExtensions = this._combineLayoutModelExtensions(collections);
			return this._loadModelExtensions(combinedExtensions);
		}

		_combineLayoutModelExtensions(collection){
			var combinedExtensions = {};
			//this is where it gets squirrely, we should edevour to simplify this... Yeah, and when monkeys fly
			collection.forEach(function(entry) {
				var extensions = entry.extensions;
				Object.keys(extensions).forEach(function(key) {
					var layoutMode = extensions[key];
					if (!combinedExtensions[key]) {
						combinedExtensions[key] = {};
					}
					Object.keys(layoutMode).forEach(function(layoutKey) {
						combinedExtensions[key][layoutKey] = layoutMode[layoutKey];
					});
				});
			});
			return combinedExtensions;
		}

		_loadModelExtensions(layoutExtensions) {
			var modelExtensions = layoutExtensions.modelExtensions;
			if (modelExtensions) {
				return Promise.all(Object.keys(modelExtensions).map((key) => {
					return ClassFactory
						.loadModule(modelExtensions[key])
						.then((module) => {
							return {
								[key]: module
							};
						}).catch((err) => {
							this.logger.error(err);
							return {};
						});
				})).then(function(results) {
					// each array member contains a single key/value so we merge them all into 1 object;
					layoutExtensions.modelExtensions = _.extend.apply(_, results);
					return layoutExtensions;
				});
			}
			return Promise.resolve(layoutExtensions);
		}

		_getCanvasExtensions() {
			var promise;
			var canvasExtension = this.dashboardApi.getCollectionConfig('canvasExtension');
			if (canvasExtension) {
				var collectionId = canvasExtension.id;
				promise = Promise.all([this.dashboardApi.findGlassCollection(collectionId)]).then(function(collections) {
					const canvasExtensions = [];
					if (collections.length > 0 && collections[0]) {
						collections[0].forEach(function(collection) {
							if (collection.active !== false) {
								canvasExtensions.push(collection);
							}
						}.bind(this));
					}
					return canvasExtensions;
				}.bind(this));
			}
			return promise || Promise.resolve();
		}

		loadBoardModelExtensions() {
			var boardModelCollection = this.dashboardApi.getCollectionConfig('boardModel');
			var collectionId = boardModelCollection.id;
			return this.dashboardApi.findGlassCollection(collectionId).then(function(extensionCollection) {
				return this._loadBoardModelExtensions(extensionCollection);
			}.bind(this));
		}

		_loadBoardModelExtensions(extensionCollection) {
			if (!extensionCollection) {
				return;
			}
			var promises = [];
			extensionCollection.forEach(function(extensionObj) {
				if (!extensionObj.name || !extensionObj.class) {
					return;
				}
				promises.push(ClassFactory.loadModule(extensionObj.class).then(function(ModelExtension) {
					return {
						'name': extensionObj.name,
						'type': extensionObj.type || 'model',
						'class': ModelExtension,
						'triggers': extensionObj.triggers || [],
						// backward compatible default is true unless value is explicitly false.
						'autoCreate': extensionObj.autoCreate !== false
					};
				}.bind(this)).catch(function(error) {
					this.logger.error(error);
				}.bind(this)));
			}, this);

			return Promise.all(promises).then((results) => {
				// Failed promises will return an undefined extension in the results. Make sure we filter it out
				return results.filter((n) => {
					return n != undefined;
				});
			});
		}
	}
	return Extensions;
});
